/**
 * One presenter photo, as the library dialog and the /admin section show it
 * (F-134): the face, the tagger's verdict as chips and a sentence, and —
 * for an admin — the two corrections the library allows: the sentence a run
 * chooses by, and whether the photo is offered at all. Nothing here removes
 * a photo; the library grows only.
 */
import {
  isPresenterPhotoOffered,
  PRESENTER_PHOTO_SUMMARY_MAX_LENGTH,
  type PresenterPhoto,
} from "@kola/shared";
import { useEffect, useState } from "react";

import { assetUrl, updatePresenterPhoto } from "../../core/api.js";
import { showToast } from "../../ui/toast.js";
import { presenterPhotoErrorMessage } from "./errors.js";
import { loadPresenterPhotos } from "./load.js";

export interface PhotoCardProps {
  photo: PresenterPhoto;
  /** An admin: the description is an input and the offer is a checkbox. */
  editable: boolean;
  /** A tagging retry is in flight for this photo. */
  tagging: boolean;
  /** Why the last tagging pass produced nothing, if it did not. */
  error: string | null;
  onRetryTag: () => void;
}

export function PhotoCard({ photo, editable, tagging, error, onRetryTag }: PhotoCardProps) {
  const tags = photo.tags;
  const offered = isPresenterPhotoOffered(photo);

  async function setUsable(usable: boolean) {
    try {
      await updatePresenterPhoto(photo.id, { usable });
      await loadPresenterPhotos();
    } catch (err) {
      showToast(presenterPhotoErrorMessage(err));
    }
  }

  async function saveSummary(summary: string) {
    try {
      await updatePresenterPhoto(photo.id, { summary });
      await loadPresenterPhotos();
      showToast("Description saved");
    } catch (err) {
      showToast(presenterPhotoErrorMessage(err));
    }
  }

  return (
    <li className={`ppl-card${offered ? "" : " ppl-card-off"}`}>
      <div className="ppl-face">
        <img src={assetUrl(photo.assetId)} alt={tags?.summary ?? "Presenter photo"} />
      </div>
      <div className="ppl-body">
        <div className="ppl-status">
          {tagging ? (
            <span className="ppl-tagging">Describing…</span>
          ) : tags !== undefined ? (
            <>
              <span className="ppl-chip ppl-mood">{tags.mood || "no mood"}</span>
              {tags.framing !== "" && <span className="ppl-chip">{tags.framing}</span>}
              {tags.gaze !== "" && <span className="ppl-chip">{tags.gaze}</span>}
            </>
          ) : (
            <span className="ppl-untagged">Not described yet</span>
          )}
          <span className="spacer" />
          {/* A retry is everyone's (the upload was theirs); a re-read of a
              photo that already has a description is an admin's call. */}
          {(tags === undefined || editable) && (
            <button type="button" className="btn btn-ghost" onClick={onRetryTag} disabled={tagging}>
              {tags !== undefined ? "Re-tag" : "Tag"}
            </button>
          )}
        </div>

        {tags !== undefined &&
          (editable ? (
            <SummaryField value={tags.summary} onCommit={(next) => void saveSummary(next)} />
          ) : (
            <p className="ppl-summary-text">{tags.summary}</p>
          ))}
        {tags !== undefined && tags.tags.length > 0 && (
          <div className="ppl-tags">
            {tags.tags.map((tag) => (
              <span key={tag} className="ppl-tag">
                {tag}
              </span>
            ))}
          </div>
        )}
        {tags !== undefined && !tags.usable && (
          <p className="ppl-issue">Not offered to runs{tags.issue ? `: ${tags.issue}` : ""}</p>
        )}
        {tags !== undefined && editable && (
          <label className="ppl-offer">
            <input
              type="checkbox"
              checked={tags.usable}
              onChange={(event) => void setUsable(event.target.checked)}
            />
            Offer to runs
          </label>
        )}
        {error !== null && (
          <p className="thumb-error" role="alert">
            {error}
          </p>
        )}
      </div>
    </li>
  );
}

/**
 * The description, edited in place and written on blur (or Enter) — the
 * thumbnail panel's words follow the same rule. Re-seeds when the tagger
 * rewrites it underneath, except while the admin is typing.
 */
function SummaryField({ value, onCommit }: { value: string; onCommit: (next: string) => void }) {
  const [draft, setDraft] = useState(value);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) setDraft(value);
  }, [value, focused]);

  const commit = () => {
    const next = draft.trim();
    if (next !== value && next.length > 0) onCommit(next);
    else setDraft(value);
  };

  return (
    <input
      className="ppl-summary"
      value={draft}
      maxLength={PRESENTER_PHOTO_SUMMARY_MAX_LENGTH}
      aria-label="What this photo shows"
      title="What a run chooses this photo by — edit if the AI misread it"
      onChange={(event) => setDraft(event.target.value)}
      onFocus={() => setFocused(true)}
      onBlur={() => {
        setFocused(false);
        commit();
      }}
      onKeyDown={(event) => {
        event.stopPropagation();
        if (event.key === "Enter" && !event.nativeEvent.isComposing) {
          event.preventDefault();
          (event.target as HTMLInputElement).blur();
        }
      }}
    />
  );
}
